import {useMutation} from '@apollo/client';

import {Grid} from '@mui/material';

import {FormikFormProps} from 'formik';
import {useSnackbar} from 'notistack';
import React, {useContext, useMemo} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {LocalizationProvider} from '@mui/x-date-pickers-pro';
import {AdapterMoment} from '@mui/x-date-pickers-pro/AdapterMoment';
import {useAccountContext} from '../../../contexts/AccountContext';
import {GOAL} from '../../../graphql/fragments';
import useAccountGoals from '../../../hooks/useAccountGoals';
import {UpdateGoal, UpdateGoalVariables} from '../../../schema-types/UpdateGoal';
import {useQueryParams} from '../../../hooks/useQueryParams';
import withAutoFormNew, {AutoFormProps} from '../../../components/AutoForm/AutoFormNew';
import {CREATE_GOAL, GET_GOAL_DATA, LIST_GOALS, UPDATE_GOAL} from '../goalsQueries';
import {getFilteredGoalFormValues, GoalFormValues} from './utils';
import Loading from '../../../components/Loading/Loading';
import {GoalButtons} from '../CreateGoal/GoalButtons';
import {GoalContext} from '../CreateGoal/CreateGoal';
import {
  EditGoalStepper,
  getSubmitGoalFunction,
  goalValidation,
  useGoalParts,
} from './GoalFormHelper';
import {useAutoForm} from '../../../components/AutoForm/useAutoForm';
import validateUUID from '../../../utils/validateUUID';

export interface GoalEditorProps extends FormikFormProps {
  setGoalFormName: (x: string) => void;
  steps: string[];
}

function GoalEditor(props: AutoFormProps<GoalFormValues> & GoalEditorProps) {
  const {setGoalFormName, steps} = props;
  const navigate = useNavigate();
  const {
    activeStep,
    setActiveStep,
    setSubmitting,
    submitting,
    setDirection,
    direction,
    requireValidation,
    setRequireValidation,
  } = useContext(GoalContext);
  const {enqueueSnackbar, closeSnackbar} = useSnackbar();
  const {parts, maxStep} = useGoalParts();
  const {values, errors, handleSubmit} = useAutoForm<GoalFormValues>();
  const exitURL = '../goals';

  const advance = React.useCallback(() => {
    closeSnackbar();
    if (requireValidation.length > 0) {
      requireValidation.forEach((m) => {
        enqueueSnackbar(m, {variant: 'error'});
      });
      return;
    }
    setDirection('forward');
    setActiveStep(Math.min(activeStep + 1, maxStep));
  }, [activeStep, setActiveStep, requireValidation]);

  const fallback = React.useCallback(() => {
    setActiveStep(Math.max(activeStep - 1, 0));
    setDirection('backward');
    setRequireValidation([]);
  }, [activeStep, setActiveStep]);

  return (
    <>
      <Grid item xs={12}>
        <EditGoalStepper activeStep={activeStep} steps={steps} />
      </Grid>
      <Grid item xs={12}>
        <form onSubmit={handleSubmit}>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            {Object.keys(parts).map((partKey, index) => {
              const SinglePart = parts[partKey].component;
              if (activeStep !== index) {
                // only rendering the selected tab now
                return null;
              }
              return (
                <SinglePart
                  key={values.contactId}
                  setRequireValidation={setRequireValidation}
                  setGoalFormName={setGoalFormName}
                />
              );
            })}
          </LocalizationProvider>
        </form>
      </Grid>
      <GoalButtons
        style={{marginTop: '5rem'}}
        activeStep={activeStep}
        handleClose={() => navigate({pathname: exitURL})}
        handleSubmit={handleSubmit}
        submitting={submitting}
        advance={advance}
        maxStep={maxStep}
        fallback={fallback}
      />
    </>
  );
}

const GoalEditorForm = withAutoFormNew<GoalFormValues, GoalEditorProps>(GoalEditor);

export interface GoalFormProps {
  setGoalFormName: (x: string) => void;
  exitURL: string;
}

function GoalForm(props: GoalFormProps) {
  const {setGoalFormName, exitURL} = props;
  const {setSubmitting} = useContext(GoalContext);
  const {reviewId} = useParams();
  const isValidReviewId = reviewId && reviewId !== 'undefined';
  const query = useQueryParams();
  const {getGoal} = useAccountGoals();
  const navigate = useNavigate();
  const {account, accountId} = useAccountContext();
  const goalId = query.get('goalid');
  const existingGoalProps = getGoal(goalId || '');
  // if in a review, go to review goals page, otherwise go to account goals page
  const afterSubmit = () => navigate({pathname: exitURL});
  const isCreating = Object.keys(existingGoalProps || {}).length === 0;
  const {enqueueSnackbar} = useSnackbar();

  const [createGoal] = useMutation(CREATE_GOAL, {
    update(cache, {data: {createGoal: createGoalData}}) {
      const {goal} = createGoalData;
      cache.modify({
        fields: {
          goals(existingGoals = []) {
            const newGoal = cache.writeFragment({
              data: goal,
              fragmentName: 'Goal',
              fragment: GOAL,
            });
            return [...existingGoals, newGoal];
          },
        },
      });
    },
  });

  const [updateGoal] = useMutation<UpdateGoal, UpdateGoalVariables>(UPDATE_GOAL, {
    // eslint-disable-next-line no-unused-vars
    refetchQueries: [
      {
        // Get the goals from the server again
        query: LIST_GOALS,
        variables: {
          accountId: account?.id || accountId,
          reviewId: validateUUID(reviewId),
        },
      },
    ],
  });
  const createGoalFromForm = (this_values: GoalFormValues) => {
    if (!isValidReviewId) {
      enqueueSnackbar('Goal needs a valid review ID', {variant: 'error'});
      // this is just  to satisfy the return type
      return new Promise<void>(() => {});
    }
    setSubmitting(true);
    return createGoal({
      variables: {
        accountId: account?.id || accountId,
      },
    })
      .then(({data}) => {
        const {goal} = data.createGoal;
        return updateGoal({
          variables: {
            goalId: goal.id,
            reviewId: validateUUID(reviewId),
            ...getFilteredGoalFormValues(this_values),
          },
        });
      })
      .then(() => {
        enqueueSnackbar('Goal created.', {variant: 'success'});
      })
      .catch(() => {
        enqueueSnackbar('Failed to create goal.', {variant: 'error'});
      })
      .finally(() => {
        setSubmitting(false);
      });
  };
  // We are editing an existing goal
  const updateGoalFromForm = (this_values: GoalFormValues) => {
    setSubmitting(true);
    return updateGoal({
      refetchQueries: [
        {
          query: GET_GOAL_DATA,
          variables: {
            accountId: account?.id || accountId,
          },
        },
      ],
      variables: {
        goalId: existingGoalProps?.id,
        reviewId: validateUUID(reviewId),
        position: existingGoalProps?.position,
        ...getFilteredGoalFormValues(this_values),
      },
    })
      .then(() => {
        enqueueSnackbar('Goal updated.', {variant: 'success'});
      })
      .catch(() => {
        enqueueSnackbar('Failed to update goal.', {variant: 'error'});
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const submitFunc = getSubmitGoalFunction(
    isCreating,
    createGoalFromForm,
    updateGoalFromForm,
    afterSubmit
  );

  const {parts} = useGoalParts();
  const steps = useMemo(() => {
    return Object.keys(parts).map((x) => parts[x].header);
  }, [parts]);

  if (goalId && !existingGoalProps) {
    return <Loading />;
  }

  return (
    <GoalEditorForm
      steps={steps}
      // initialValueOverrides={exampleInitial}
      initialValueOverrides={existingGoalProps as Partial<GoalFormValues>}
      validate={goalValidation}
      formFields={[]}
      setGoalFormName={setGoalFormName}
      submit={submitFunc}
    />
  );
}

export default GoalForm;
